/**
 * Link picker: the "Link to" dialog that turns a nav type plus a target into a `cms://`
 * link expression.
 *
 * SURF-4809 moved this off Angular. It was `plugins/wysiwyg/cms/web/internalLinkService.js`
 * plus `internalLinkModalController.js` and `internalLinkModal.html`, reached from React
 * through the `showLinkSearch` prop that `root_controller` handed to `cmsReactContext`, and
 * reached from the Froala toolbar button by pulling `internalLinkService` out of the Angular
 * injector. Both callers now land here.
 *
 * The **contract is unchanged** and must stay that way while any caller is still Angular:
 * resolve to `{ returnVal, targetLabel, sso }`, or to `null` when the dialog is dismissed.
 */

/** What a resolved external-link target looks like before `denormalizeHref` unwraps it. */
export const EXTERNAL_LINK_PREFIX = 'cms://externalLink?href=';

/**
 * Two return shapes, kept from the Angular service:
 *
 * - `normalizedLink` gives the raw `cms://` expression. Every React form field wants this,
 *   because it is what gets stored and what the app resolves.
 * - `link` gives a real URL for external links, because Froala writes the value into an
 *   `<a href>` where `cms://externalLink?href=...` would not be clickable.
 */
export const RETURN_TYPE = {
  link: 'link',
  normalizedLink: 'normalizedLink',
};

/** True for a target that resolves to an external URL, which is the only SSO case. */
export const isExternalLinkTarget = (value) =>
  typeof value === 'string' && value.startsWith(EXTERNAL_LINK_PREFIX);

/** True when a saved navUrl already carries the SSO flag. */
export const hasRequireSso = (value) =>
  typeof value === 'string' && /[&?]requireSSO=true(?:&|$)/.test(value);

/**
 * Unwrap `cms://externalLink?href=<encoded>` back to the URL itself.
 *
 * Only the `href` param is taken. Stopping at the first `&` is deliberate: trailing params
 * such as `requireSSO=true` or a link type's additional properties must not be folded into
 * the decoded URL. Carried over from the Angular controller, where getting this wrong
 * produced hrefs with query strings glued onto them.
 */
export const denormalizeHref = (href) => {
  if (!isExternalLinkTarget(href)) return href;
  const tail = href.slice(EXTERNAL_LINK_PREFIX.length);
  return decodeURIComponent(tail.split('&')[0]);
};

/**
 * Was this link saved as an SSO link?
 *
 * `current` is whatever the caller had: the `<a>` element Froala hands over from
 * `editor.link.get()`, or a plain href string from a form field. An SSO link is marked by
 * `data-sso="true"` on the anchor, or by `requireSSO=true` on the navUrl.
 */
export const readInitialSso = (current) => {
  if (!current) return false;
  if (typeof current.getAttribute === 'function') return current.dataset?.sso === 'true';
  return hasRequireSso(current);
};

/**
 * Build the link expression from the dialog's current selection.
 *
 * Four cases, in the order the Angular controller resolved them:
 *  1. a target chosen from a second-level (additional) selector,
 *  2. a target chosen from the first selector,
 *  3. a link type that carries its own fixed `href` (no target to choose),
 *  4. a free-text href typed into the input, wrapped as `cms://<type>?href=...`.
 *
 * Any `additionalProperties` the type declares are appended as query params.
 */
export const buildHref = ({ linkType, selectedTarget, additionalTargetValue, typedHref }) => {
  let href;
  let targetLabel;
  if (additionalTargetValue) {
    href = additionalTargetValue;
    targetLabel = selectedTarget?.label;
  } else if (selectedTarget) {
    href = selectedTarget.value;
    targetLabel = selectedTarget.label;
  } else if (linkType?.href) {
    href = linkType.href;
    targetLabel = linkType.label;
  } else {
    href = `cms://${linkType?.value}?href=${encodeURIComponent(typedHref || '')}`;
    targetLabel = linkType?.label;
  }
  for (const property of linkType?.additionalProperties || []) {
    if (!property.value) continue;
    href += href.includes('?') ? '&' : '?';
    href += `${property.name}=${encodeURIComponent(property.value)}`;
  }
  return { href, targetLabel };
};
